import { useMemo } from 'react'
import { isEmpty } from 'remeda'
import { useMediaQuery } from 'usehooks-ts'
import { ComplementaryProducts } from '@components/ComplementaryProducts/ComplementaryProducts'
import { useCart } from '@velasca/hooks/use-cart'
import { useCartSuggestedProducts } from '@velasca/hooks/use-cart-suggested-products'
import type { Image } from '@velasca/types'
import { getCompleteTheLookEditorialImage } from '@velasca/utils/get-complete-the-look-editorial-image'
import { trackEvent } from '@velasca/utils/track-event'
import { Content } from './DesktopSuggestedProducts.styles'

interface DesktopSuggestedProductsProps {
  editorialImage?: Image
}

const ULTRA_CONDENSED_LAYOUT_MEDIA_QUERY = '(max-width: 1600px)'

export const DesktopSuggestedProducts = ({
  editorialImage,
}: DesktopSuggestedProductsProps) => {
  const { data, error, onAddToCartAddeventName, onQuickAddEventName, loading } =
    useCartSuggestedProducts()
  const { suggestedProductsHandle, data: cartData } = useCart()
  const isUltraCondensedLayout = useMediaQuery(
    ULTRA_CONDENSED_LAYOUT_MEDIA_QUERY,
  )

  const editorialImageMemo = useMemo(() => {
    const latestCartItemNoGiftCard = cartData?.items.find(
      (item) => !item.gift_card,
    )

    return getCompleteTheLookEditorialImage({
      product: {
        editorialImage:
          latestCartItemNoGiftCard?.crossSellProductsEditorialImage,
        images: latestCartItemNoGiftCard?.images ?? [],
      },
      defaultImage: editorialImage?.src,
    })
  }, [cartData?.items, editorialImage?.src])

  // Since we don't know if recent or complementary have some data, we just cannot show a loading state with a skeleton. That' why we are ignoring the loading state and simply return null
  if (error || isEmpty(data) || loading) {
    return null
  }

  return (
    <Content
      legacyMode={!editorialImage}
      isUltraCondensedLayout={isUltraCondensedLayout}
    >
      <ComplementaryProducts
        layout={isUltraCondensedLayout ? 'ultra-condensed' : 'standard'}
        products={data}
        editorialImage={editorialImageMemo}
        breakpoints={{
          1280: { slidesPerView: 2.5 },
          1440: { slidesPerView: 3.1 },
          1920: { slidesPerView: 3.5 },
        }}
        onQuickAdd={() => {
          onAddToCartAddeventName &&
            trackEvent({
              googleAnalytics: {
                eventName: onQuickAddEventName,
                data: { handle: suggestedProductsHandle },
              },
            })
        }}
        onAddToCart={(handle) => {
          onAddToCartAddeventName &&
            trackEvent({
              googleAnalytics: {
                eventName: onAddToCartAddeventName,
                data: { handle },
              },
            })
        }}
      />
    </Content>
  )
}
